import { useEffect, useRef } from 'react';

/**
 * Custom hook to detect clicks outside of specified elements
 * @param {Array<React.RefObject>} refs - Array of refs to check against
 * @param {Function} handler - Callback function to execute when click is outside
 * @param {boolean} isEnabled - Whether the listener should be active (default: true)
 */
export const useClickOutside = (refs, handler, isEnabled = true) => {
  // Store latest values in refs to avoid stale closures
  const refsRef = useRef(refs);
  const handlerRef = useRef(handler);

  useEffect(() => {
    refsRef.current = refs;
    handlerRef.current = handler;
  }, [refs, handler]);

  useEffect(() => {
    if (!isEnabled) return;

    const handleClickOutside = (event) => {
      // Check if click was inside any of the referenced elements
      const clickedInside = refsRef.current.some(ref => 
        ref?.current && ref.current.contains(event.target)
      );

      // If click was outside all referenced elements, call handler
      if (!clickedInside) {
        handlerRef.current(event);
      }
    };

    // Use mousedown instead of click to catch clicks before they bubble
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEnabled]);
};

export default useClickOutside;
